/**
 * @fileoverview Hook for subscribing to a legacy migration manifest override from Dexie.
 * @module system-web/hooks/useManifestOverride
 *
 * Responsibilities:
 * - Subscribe to a manifest override document via Dexie liveQuery.
 * - Expose save and reset mutations that post through system-api.
 *
 * Dependencies:
 * - dexie (liveQuery)
 * - @firesquirrel/local-db
 * - ../services/legacyManifestService
 *
 * Architecture:
 * - Override is local-first: liveQuery on `legacy_migration_manifest_overrides`.
 * - Mutations (saveOverride, resetOverride) call through system-api; Dexie receives
 *   the authoritative result via the next system sync pull.
 * - liveQuery observed function performs ONLY reads — no writes (§liveQuery contract).
 *
 * Notes:
 * - The effective manifest computation (resolveEffectiveManifest) is done in the component
 *   or service layer — this hook only surfaces the raw override document.
 * - useLegacySchema is a separate hook for the online-exception schema fetch.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { liveQuery } from 'dexie';
import { getSystemDb } from '@firesquirrel/local-db';
import {
  getOverride,
  saveOverride,
  resetOverride,
  type ManifestOverrideRow,
  type SaveOverrideInput,
} from '../services/legacyManifestService';

/**
 * @contract ManifestOverrideState
 *
 * Purpose:
 * - Shape returned by useManifestOverride.
 *
 * Fields:
 * - override: The current manifest override doc from Dexie, or null if none exists.
 * - isLoading: True while the initial Dexie query is resolving.
 * - isSaving: True while a save or reset mutation is in-flight.
 * - error: Non-null when the Dexie subscription or mutation fails.
 * - save: Async mutation to save the override; throws on validation or conflict errors.
 * - reset: Async mutation to soft-delete the override.
 *
 * Rules:
 * - save() 409 = version conflict; caller must reload override and retry.
 * - save() 422 includes per-field issues in the thrown error.
 *
 * Sync:
 * - Dexie mirrors system Mongo `legacyMigrationManifestOverrides` via system sync pull.
 */
export interface ManifestOverrideState {
  override: ManifestOverrideRow | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  save: (input: SaveOverrideInput) => Promise<ManifestOverrideRow>;
  reset: () => Promise<void>;
}

/**
 * @hook useManifestOverride
 *
 * Purpose:
 * - Provide reactive access to the manifest override for a given dataset, plus
 *   save and reset mutations.
 *
 * Returns:
 * - override, isLoading, isSaving, error, save, reset
 *
 * Side Effects:
 * - Subscribes a Dexie liveQuery for the override (torn down on unmount / dep change).
 * - save() / reset() issue HTTP mutations via legacyManifestService.
 *
 * Offline Behavior:
 * - Override reads are offline-capable (Dexie).
 * - save() / reset() require connectivity (online mutations).
 */
export function useManifestOverride(datasetKey: string | null): ManifestOverrideState {
  const [override, setOverride] = useState<ManifestOverrideRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  // liveQuery subscription for the override — pure read, no writes.
  useEffect(() => {
    if (!datasetKey) {
      setOverride(null);
      setIsLoading(false);
      return;
    }

    let subscription: { unsubscribe: () => void } | null = null;
    let cancelled = false;
    setIsLoading(true);

    getSystemDb()
      .then((db) => {
        if (cancelled) return;
        subscription = liveQuery(
          () => getOverride(datasetKey),
        ).subscribe({
          next: (row) => {
            if (!cancelled && !unmountedRef.current) {
              setOverride(row);
              setIsLoading(false);
            }
          },
          error: (err: unknown) => {
            if (!cancelled && !unmountedRef.current) {
              const msg = err instanceof Error ? err.message : 'Failed to load manifest override';
              console.warn('[useManifestOverride] liveQuery error:', err);
              setError(msg);
              setIsLoading(false);
            }
          },
        });
        void db;
      })
      .catch((err) => {
        if (!cancelled && !unmountedRef.current) {
          const msg = err instanceof Error ? err.message : 'Failed to open Dexie';
          console.warn('[useManifestOverride] getSystemDb error:', err);
          setError(msg);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, [datasetKey]);

  const save = useCallback(
    async (input: SaveOverrideInput): Promise<ManifestOverrideRow> => {
      if (!datasetKey) throw new Error('datasetKey is required to save an override');
      setIsSaving(true);
      setError(null);
      try {
        const result = await saveOverride(datasetKey, input);
        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to save manifest override';
        if (!unmountedRef.current) setError(msg);
        throw err;
      } finally {
        if (!unmountedRef.current) setIsSaving(false);
      }
    },
    [datasetKey],
  );

  const reset = useCallback(async (): Promise<void> => {
    if (!datasetKey) throw new Error('datasetKey is required to reset an override');
    setIsSaving(true);
    setError(null);
    try {
      await resetOverride(datasetKey);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to reset manifest override';
      if (!unmountedRef.current) setError(msg);
      throw err;
    } finally {
      if (!unmountedRef.current) setIsSaving(false);
    }
  }, [datasetKey]);

  return { override, isLoading, isSaving, error, save, reset };
}
